import { CognitoUserPool, CognitoUser, AuthenticationDetails } from 'amazon-cognito-identity-js';
import config from '../config';

const pool = new CognitoUserPool({
  UserPoolId: config.cognito.userPoolId,
  ClientId: config.cognito.clientId,
});

export const signUp = (email, password) =>
  new Promise((resolve, reject) =>
    pool.signUp(email, password, [], null, (err, result) =>
      err ? reject(err) : resolve(result)
    )
  );

export const confirmSignUp = (email, code) =>
  new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: pool });
    user.confirmRegistration(code, true, (err, result) =>
      err ? reject(err) : resolve(result)
    );
  });

export const signIn = (email, password, newPassword) =>
  new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: pool });
    user.authenticateUser(
      new AuthenticationDetails({ Username: email, Password: password }),
      {
        onSuccess: (session) => resolve(session),
        onFailure: (err) => reject(err),
        newPasswordRequired: () => {
          if (newPassword) {
            user.completeNewPasswordChallenge(newPassword, {}, {
              onSuccess: (session) => resolve(session),
              onFailure: (err) => reject(err),
            });
          } else {
            reject({ code: 'NewPasswordRequired', message: 'New password required' });
          }
        },
      }
    );
  });

export const signOut = () => {
  const user = pool.getCurrentUser();
  if (user) user.signOut();
};

// Shared in-flight session lookup. Without this, concurrent API calls each
// trigger their own refreshSession() network request when the ID token is
// expired; those refreshes race and some reject, producing intermittent
// "Not signed in" / "Invalid session" errors on whichever call lost the race.
let sessionPromise = null;

const resolveSession = () =>
  new Promise((resolve, reject) => {
    const user = pool.getCurrentUser();
    if (!user) return reject(new Error('Not signed in'));
    // getSession() returns the cached session if valid, or transparently
    // refreshes it using the refresh token if the ID token has expired.
    user.getSession((err, session) => {
      if (err || !session?.isValid()) return reject(err || new Error('Invalid session'));
      resolve(session);
    });
  });

export const getToken = () => {
  // Coalesce concurrent callers onto a single getSession()/refresh cycle.
  if (!sessionPromise) {
    sessionPromise = resolveSession().finally(() => { sessionPromise = null; });
  }
  return sessionPromise.then((session) => session.getIdToken().getJwtToken());
};

export const getCurrentUser = () => pool.getCurrentUser();
